import {
  Component,
  computed,
  effect,
  HostListener,
  inject,
  signal,
  Signal,
  WritableSignal,
} from '@angular/core';
import { NgStyle } from '@angular/common';
import {
  FormBuilder,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { CorporateDesignService } from '../../../../services/corporate-design/corporate-design.service';
import { OnDemandSelectionStateService } from '../../../../services/on-demand/on-demand-selection-state.service';
import {
  EEntityType,
  EMessageCode,
  EMessageType,
  EOrderOrigin,
  ERelationType,
  EFahrgasttyp,
  IOnDemandCheckoutRequest,
  IOnDemandPaymentForm,
  MINIMUM_BOOKING_LOADING_DURATION_MS,
  PaymentMethod,
} from '@web/dtos';
import { ERoute } from '../../../../constants/routes.enum';
import { UserStore } from '../../../../services/api/user/user/user-store.service';
import { OnDemandBookingStore } from '../../../../services/on-demand/on-demand-booking-store.service';
import { SalutationStore } from '../../../../services/api/salutation/salutation-store.service';
import { LanguagePipe } from '../../../../pipes/language/language.pipe';
import { FilterTextsPipe } from '../../../../pipes/filterTexts/filterTexts.pipe';
import { WebTextStore } from '../../../../services/api/text/web-text-store.service';
import { LoggingService } from '../../../../services/logging/logging.service';
import { OnDemandTicketConfigurationSummaryComponent } from '../on-demand-ticket-configuration-summary/on-demand-ticket-configuration-summary.component';
import { CheckboxComponent } from '../../../../components/data-input/form-inputs/checkbox/checkbox.component';
import { DropDownComponent } from '../../../../components/data-input/form-inputs/drop-down/drop-down.component';
import { TextInputComponent } from '../../../../components/data-input/form-inputs/text-input/text-input.component';
import { DateInputComponent } from '../../../../components/data-input/form-inputs/date-input/date-input/date-input.component';
import { LoadingAnimationComponent } from '../../../../components/feedback/loading-animation/loading-animation.component';
import { SingleSelectComponent } from '../../../../components/data-input/form-inputs/single-select/single-select.component';
import { PaymentMethodDisplayComponent } from '../../../../components/data-display/payment-method-display/payment-method-display.component';
import { PaymentProviderStore } from '../../../../services/api/payment-provider/payment-provider-store.service';
import { OnDemandPricingStore } from '../../../../services/on-demand/on-demand-pricing.service';
import { firstValueFrom } from 'rxjs';
import { ReconRouteStore } from '../../../../services/api/journey-planning/recon/recon-route-store.service';
import { OnDemandAvailabilityStore } from '../../../../services/api/on-demand/on-demand-availability-store.service';
import { OnDemandRelationProductGroupStore } from '../../../../services/api/on-demand/on-demand-relation-product-group-store.service';
import { OnDemandComfortPaymentStore } from '../../../../services/api/on-demand/on-demand-comfort-payment-store.service';
import { ButtonComponent } from '../../../../components/feedback/button/button.component';
import { formatDateOnly, parseIsoDate } from '@web/shared-functions/date';
import { buildOnDemandOrderData } from '@web/shared-functions/on-demand';
import { OnDemandOrderGateway } from '../../../../services/api/on-demand/on-demand-order.gateway';
import { ErrorHandlingService } from '../../../../services/errorHandling/error-handling.service';
import { MessageService } from '../../../../services/messages/messages.service';

@Component({
  selector: 'aw-on-demand-ticket-configuration-payment',
  templateUrl: './on-demand-ticket-configuration-payment.component.html',
  imports: [
    NgStyle,
    ReactiveFormsModule,
    LanguagePipe,
    FilterTextsPipe,
    OnDemandTicketConfigurationSummaryComponent,
    CheckboxComponent,
    DropDownComponent,
    TextInputComponent,
    DateInputComponent,
    LoadingAnimationComponent,
    SingleSelectComponent,
    PaymentMethodDisplayComponent,
    ButtonComponent,
  ],
})
export class OnDemandTicketConfigurationPaymentComponent {
  protected readonly EFahrgasttyp = EFahrgasttyp;
  protected readonly ERelationType = ERelationType;
  protected readonly EEntityType = EEntityType;

  protected readonly corporateDesign = inject(CorporateDesignService);
  protected readonly selectionState = inject(OnDemandSelectionStateService);
  protected readonly salutationStore = inject(SalutationStore);
  protected readonly textStore = inject(WebTextStore);
  protected readonly userStore = inject(UserStore);
  private readonly formBuilder = inject(FormBuilder);
  private readonly languagePipe = inject(LanguagePipe);
  private readonly bookingStore = inject(OnDemandBookingStore);
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly paymentProviderStore = inject(PaymentProviderStore);
  private readonly pricingStore = inject(OnDemandPricingStore);
  private readonly reconRouteStore = inject(ReconRouteStore);
  private readonly availabilityStore = inject(OnDemandAvailabilityStore);
  private readonly productGroupStore = inject(
    OnDemandRelationProductGroupStore
  );
  private readonly comfortPaymentStore = inject(OnDemandComfortPaymentStore);
  private readonly textFilterPipe = inject(FilterTextsPipe);
  private readonly onDemandOrderGateway: OnDemandOrderGateway =
    new OnDemandOrderGateway();
  private readonly errorHandlingService = inject(ErrorHandlingService);
  private readonly messageService = inject(MessageService);

  protected readonly isBooking = signal(false);
  protected readonly isTransactionInProgress: WritableSignal<boolean> =
    signal(false);
  protected readonly paymentFailed: WritableSignal<boolean> = signal(false);
  private readonly allPaymentMethods: Signal<PaymentMethod[]> =
    this.paymentProviderStore.paymentMethods;
  protected readonly paymentMethods: Signal<PaymentMethod[]> = computed(() =>
    this.allPaymentMethods().filter(
      paymentMethod =>
        !('stateType' in paymentMethod) || paymentMethod.stateType === 'ACTIVE'
    )
  );
  protected readonly isLoadingPaymentMethods: Signal<boolean> =
    this.paymentProviderStore.isLoadingPaymentMethods;
  protected readonly paymentMethodControl: FormControl<string> =
    this.formBuilder.nonNullable.control('');
  protected readonly allPassengersPersonalData = computed(() =>
    this.selectionState.allPassengersPersonalData()
  );
  protected readonly salutationOptions = computed(() =>
    this.salutationStore.salutations().map(salutation => ({
      id: String(salutation.id),
      name: this.languagePipe.transform(salutation.texts),
    }))
  );
  protected readonly paymentForm: FormGroup<IOnDemandPaymentForm> =
    this.formBuilder.group<IOnDemandPaymentForm>({
      salutation: this.formBuilder.nonNullable.control(
        {
          value: this.salutationStore.getId(this.userStore.user()?.salutation),
          disabled: true,
        },
        Validators.required
      ),
      firstName: this.formBuilder.nonNullable.control(
        { value: this.userStore.user()?.firstName ?? '', disabled: true },
        Validators.required
      ),
      lastName: this.formBuilder.nonNullable.control(
        { value: this.userStore.user()?.lastName ?? '', disabled: true },
        Validators.required
      ),
      birthDate: this.formBuilder.nonNullable.control<Date | undefined>(
        {
          value: parseIsoDate(this.userStore.user()?.birthDate),
          disabled: true,
        },
        Validators.required
      ),
      email: this.formBuilder.nonNullable.control(
        { value: this.userStore.user()?.email ?? '', disabled: true },
        [Validators.required, Validators.email]
      ),
      phone: this.formBuilder.nonNullable.control(
        { value: this.userStore.user()?.phoneNumber ?? '', disabled: true },
        Validators.required
      ),
      agreement: this.formBuilder.nonNullable.control(
        false,
        Validators.requiredTrue
      ),
    });

  constructor(private readonly log: LoggingService) {
    this.restorePaymentCustomerData();

    const isSuccessfulOnDemandRedirect =
      this.route.snapshot.queryParams['onDemandPayment'] === 'success';
    const pendingOrderId = this.selectionState.pendingOrderId();
    if (
      isSuccessfulOnDemandRedirect &&
      pendingOrderId &&
      this.selectionState.orderConfirmationPending()
    ) {
      void this.finalizeSuccessfulPayment(pendingOrderId);
    } else if (
      pendingOrderId &&
      this.selectionState.orderConfirmationPending()
    ) {
      void this.abortCheckout(pendingOrderId);
    }

    effect(() => {
      const user = this.userStore.user();
      this.salutationStore.salutations();
      if (!user) {
        return;
      }

      this.paymentForm.patchValue({
        salutation: this.salutationStore.getId(user.salutation),
        firstName: user.firstName ?? '',
        lastName: user.lastName ?? '',
        birthDate: parseIsoDate(user.birthDate),
        email: user.email ?? '',
        phone: user.phoneNumber ?? '',
      });
    });

    effect(() => {
      const userId = this.userStore.user()?.userId;
      if (userId) {
        this.paymentProviderStore.getPaymentMethods();
      }
    });

    effect(() => {
      const paymentMethods = this.paymentMethods();
      if (
        paymentMethods.length > 0 &&
        !paymentMethods.some(
          paymentMethod => paymentMethod.id === this.paymentMethodControl.value
        )
      ) {
        const favoritePaymentMethod = paymentMethods.find(
          paymentMethod => 'favorite' in paymentMethod && paymentMethod.favorite
        );
        this.paymentMethodControl.setValue(
          favoritePaymentMethod?.id ?? paymentMethods[0].id,
          { emitEvent: false }
        );
      }
    });
  }

  @HostListener('window:focus')
  protected reloadPaymentMethods(): void {
    if (this.userStore.user()?.userId) {
      this.paymentProviderStore.reloadMethods();
    }
  }

  protected addPaymentMethod(): void {
    const user = this.userStore.user();
    if (!user?.userId) {
      return;
    }

    this.persistCheckoutState();

    this.paymentProviderStore
      .addPaymentMethods(EOrderOrigin.OnDemandTicketConfig, user)
      .subscribe({
        next: response => {
          const redirectUrl = response.data?.redirectUrl;
          if (!redirectUrl) {
            this.log.error(
              'LOGPAY did not return a payment-method redirect URL.'
            );
            return;
          }
          window.location.href = redirectUrl;
        },
        error: error => {
          this.log.error('Adding a LOGPAY payment method failed.', error);
        },
      });
  }

  private persistCheckoutState(): void {
    const trip = this.reconRouteStore.reconRoute();
    const operatorMapping = this.availabilityStore.matchedMapping();
    const productGroups = this.productGroupStore.productGroups();
    const systemPayments = this.comfortPaymentStore.systemPayments();
    const customerData = this.paymentForm.getRawValue();

    this.selectionState.checkoutTotalAmount.set(
      this.pricingStore.totalAmount()
    );
    if (trip) {
      this.selectionState.checkoutTrip.set(trip);
    }
    if (operatorMapping) {
      this.selectionState.checkoutOperatorMapping.set(operatorMapping);
    }
    if (productGroups.length > 0) {
      this.selectionState.checkoutProductGroups.set(productGroups);
    }
    if (systemPayments.length > 0) {
      this.selectionState.checkoutSystemPayments.set(systemPayments);
    }
    this.selectionState.paymentCustomerData.set({
      ...customerData,
      birthDate: formatDateOnly(customerData.birthDate),
    });
    const customerReference = this.userStore.userOrGuestId();
    if (customerReference) {
      this.selectionState.paymentCustomerReference.set(customerReference);
      this.selectionState.paymentAsGuest.set(!this.userStore.user());
    }
    this.selectionState.persistPaymentState();
  }

  private restorePaymentCustomerData(): void {
    const customerData = this.selectionState.paymentCustomerData();
    if (!customerData) {
      return;
    }

    this.paymentForm.patchValue({
      ...customerData,
      birthDate: parseIsoDate(customerData.birthDate),
    });
  }

  protected async navigateNext(): Promise<void> {
    if (this.paymentForm.invalid || this.isTransactionInProgress()) {
      this.paymentForm.markAllAsTouched();
      return;
    }

    const sessionTimeout = this.selectionState.getReservationSessionTimeout();
    const bookingReference =
      this.selectionState.reservationBookingResponse()?.bookingRef;
    const calculatedTotalAmount = this.pricingStore.totalAmount();
    const totalAmount =
      calculatedTotalAmount > 0
        ? calculatedTotalAmount
        : (this.selectionState.checkoutTotalAmount() ?? 0);
    const customerData = this.paymentForm.getRawValue();
    this.persistCheckoutState();
    if (
      sessionTimeout <= 0 ||
      !bookingReference ||
      !Number.isFinite(totalAmount) ||
      totalAmount <= 0
    ) {
      this.paymentFailed.set(true);
      this.log.warn('On-demand checkout data is incomplete or expired.');
      return;
    }

    const loadingStartedAt = Date.now();
    this.selectionState.pendingOrderId.set(null);
    this.selectionState.orderConfirmationPending.set(false);
    this.paymentFailed.set(false);
    this.isBooking.set(true);
    this.isTransactionInProgress.set(true);

    try {
      const bookingRequest = this.bookingStore.getConfirmationRequest(
        `${customerData.firstName} ${customerData.lastName}`,
        customerData.email,
        customerData.phone
      );
      const checkoutRequest: IOnDemandCheckoutRequest = {
        bookingReference,
        totalAmount,
        sessionTimeout,
        paymentMethodId:
          !this.isGuestPayment() && this.paymentMethodControl.value
            ? this.paymentMethodControl.value
            : undefined,
      };
      const response = await firstValueFrom(
        this.onDemandOrderGateway.startCheckout(
          {
            order: this.buildOrderData(),
            booking: bookingRequest,
            payment: checkoutRequest,
          },
          this.isGuestPayment()
        )
      );
      const checkout = response.data;
      if (response.message?.type === 'error' || !checkout?.orderId) {
        throw new Error('On-demand checkout could not be started.');
      }

      this.selectionState.pendingOrderId.set(checkout.orderId);
      this.selectionState.confirmBookingResponse.set(checkout.booking);
      this.selectionState.orderConfirmationPending.set(!checkout.completed);
      this.selectionState.persistPaymentState();
      await this.waitForMinimumBookingLoadingDuration(loadingStartedAt);

      if (checkout.redirectUrl) {
        window.location.assign(checkout.redirectUrl);
      } else if (checkout.completed) {
        this.selectionState.pendingOrderId.set(null);
        this.selectionState.persistPaymentState();
        await this.navigateToCheckout();
      } else {
        throw new Error('On-demand payment was not completed.');
      }
    } catch (err) {
      await this.waitForMinimumBookingLoadingDuration(loadingStartedAt);
      this.paymentFailed.set(true);
      this.log.error('On-demand booking or payment failed', err);
      this.errorHandlingService.handleFatal(
        this.getOnDemandText(ERelationType.onDemandOrderCancellationCompleted)
      );
    } finally {
      this.isBooking.set(false);
      this.isTransactionInProgress.set(false);
    }
  }

  private async finalizeSuccessfulPayment(orderId: string): Promise<void> {
    this.isTransactionInProgress.set(true);
    this.paymentFailed.set(false);
    this.selectionState.orderConfirmationPending.set(true);
    this.selectionState.persistPaymentState();

    try {
      const response = await firstValueFrom(
        this.onDemandOrderGateway.completeCheckout(
          orderId,
          this.isGuestPayment(),
          this.getPaymentCustomerReference()
        )
      );
      if (response.message?.type === 'error' || !response.data?.completed) {
        throw new Error('On-demand checkout could not be completed.');
      }
      this.selectionState.pendingOrderId.set(null);
      this.selectionState.orderConfirmationPending.set(false);
      this.selectionState.persistPaymentState();
      await this.navigateToCheckout();
    } catch (error) {
      this.paymentFailed.set(true);
      this.selectionState.pendingOrderId.set(null);
      this.selectionState.orderConfirmationPending.set(false);
      this.selectionState.persistPaymentState();
      this.log.error('On-demand payment confirmation failed', error);
      this.errorHandlingService.handleFatal(
        this.getOnDemandText(ERelationType.onDemandOrderCancellationCompleted)
      );
    } finally {
      this.isTransactionInProgress.set(false);
    }
  }

  private async abortCheckout(orderId: string): Promise<void> {
    this.isTransactionInProgress.set(true);
    try {
      const response = await firstValueFrom(
        this.onDemandOrderGateway.abortCheckout(
          orderId,
          this.isGuestPayment(),
          this.getPaymentCustomerReference()
        )
      );
      if (response.message?.type === 'error' || !response.data?.cancelled) {
        throw new Error('On-demand checkout could not be cancelled.');
      }
    } catch (error) {
      this.log.error('Aborting on-demand checkout failed', error);
    } finally {
      this.selectionState.pendingOrderId.set(null);
      this.selectionState.orderConfirmationPending.set(false);
      this.selectionState.persistPaymentState();
      this.isTransactionInProgress.set(false);
      this.errorHandlingService.handleFatal(
        this.getOnDemandText(ERelationType.onDemandOrderCancellationCompleted)
      );
    }
  }

  private buildOrderData() {
    const trip =
      this.reconRouteStore.reconRoute() ?? this.selectionState.checkoutTrip();
    const operatorMapping =
      this.availabilityStore.matchedMapping() ??
      this.selectionState.checkoutOperatorMapping();

    return buildOnDemandOrderData({
      customerReference: this.getPaymentCustomerReference(),
      customer: this.selectionState.paymentCustomerData(),
      reservation: this.selectionState.reservationBookingResponse(),
      booking:
        this.selectionState.confirmBookingResponse() ??
        this.selectionState.reservationBookingResponse(),
      trip,
      connection: this.selectionState.selectedConnectionOption(),
      operatorMapping,
      onDemandLeg:
        this.availabilityStore.onDemandLeg() ??
        trip?.legs.find(leg => leg.operator.id === operatorMapping?.operatorId),
      selections: this.selectionState.passengerTicketSelections(),
      passengerData: this.selectionState.allPassengersPersonalData(),
      extrasPerPassenger: this.selectionState.extrasPerPassenger(),
      totalAmount:
        this.selectionState.checkoutTotalAmount() ??
        this.pricingStore.totalAmount(),
      paymentMethod: this.paymentMethods().find(
        method => method.id === this.paymentMethodControl.value
      ),
      onDemandProduct: this.getOnDemandText(ERelationType.onDemandTicket),
      comfortProduct: this.getOnDemandText(ERelationType.comfortSurcharge),
      technicalCancellationReason: this.getOnDemandText(
        ERelationType.onDemandOrderTechnicalCancellationReason
      ),
      labels: {
        passenger: this.getOnDemandText(ERelationType.onDemandPassenger),
        operator: this.getOnDemandText(ERelationType.onDemandOrderOperator),
        departure: this.getText(ERelationType.departure, EEntityType.generic),
        arrival: this.getText(ERelationType.arrival, EEntityType.generic),
        changeableUntil: this.getOnDemandText(
          ERelationType.onDemandOrderChangeableUntil
        ),
        cancellableUntil: this.getOnDemandText(
          ERelationType.onDemandOrderCancellableUntil
        ),
        notCancellable: this.getOnDemandText(
          ERelationType.onDemandOrderNotCancellable
        ),
        existingPublicTransportTickets: this.getOnDemandText(
          ERelationType.onDemandOrderExistingPublicTransportTickets
        ),
        extras: this.getOnDemandText(ERelationType.onDemandExtras),
      },
      getPrice: (type, passengerType) =>
        this.pricingStore.getPrice(type, passengerType),
      getPublicTransportTicketLabel: passengerType =>
        this.pricingStore.getPublicTransportTicketLabel(passengerType),
    });
  }

  private getOnDemandText(relationType: ERelationType): string {
    return this.getText(relationType, EEntityType.onDemandTicketConfiguration);
  }

  private getPaymentCustomerReference(): string {
    return (
      this.selectionState.paymentCustomerReference() ??
      this.userStore.userOrGuestId()
    );
  }

  private isGuestPayment(): boolean {
    return this.selectionState.paymentAsGuest() ?? !this.userStore.user();
  }

  private getText(
    relationType: ERelationType,
    entityType: EEntityType
  ): string {
    return this.languagePipe.transform(
      this.textFilterPipe.transform(
        this.textStore.texts(),
        relationType,
        entityType
      )
    );
  }

  private async navigateToCheckout(): Promise<void> {
    const navigationSuccessful = await this.router.navigate([
      `/${ERoute.onDemandTicketConfiguration}/${ERoute.checkout}`,
    ]);
    if (!navigationSuccessful) {
      throw new Error('Navigation to on-demand checkout failed.');
    }
    this.messageService.add({
      type: EMessageType.SUCCESS,
      code: EMessageCode.TICKETS_PAYMENT_SUCCESS,
    });
  }

  private async waitForMinimumBookingLoadingDuration(
    loadingStartedAt: number
  ): Promise<void> {
    const remainingDuration =
      MINIMUM_BOOKING_LOADING_DURATION_MS - (Date.now() - loadingStartedAt);

    if (remainingDuration > 0) {
      await new Promise(resolve => setTimeout(resolve, remainingDuration));
    }
  }
}
